import { MoneyParts, MoneyText } from '@/components/MoneyText';
import { TabularText } from '@/components/TabularText';
import { useEffect, useRef, useState } from 'react';
import {
  Alert,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { DialogDoneButton } from '@/components/DialogDoneButton';
import { useDialogFullScreen } from '@/shell/useDialogFullScreen';
import {
  allocationRulesApi,
  type DryRun,
  type DryRunMatch,
  type RuleDraft,
} from '@/api/allocationRules';
import { ErrorCodes, isApiError } from '@/api/errors';
import { formatCivilDate } from '@/i18n/format';
import { useLocale } from '@/i18n/useLocale';
import { useOwnedEnvelopes } from '@/features/accounts/useOwnedEnvelopes';
import { civilInZone, civilSpanDays, envelopeLabel } from './summarize';
import { addCivilDays } from '@/shared/servedWindow';

/** The dry run's title id: the host dialog's accessible name while it shows. */
export const DRY_RUN_TITLE_ID = 'rules-dry-run-title';
const MAX_DAYS = 366;

export type DryRunSource = { rule: RuleDraft } | { ruleId: number; transfer?: boolean };

interface DryRunPanelProps {
  accountId: number;
  currency: string | null;
  timeZone: string;
  source: DryRunSource;
  onClose: () => void;
  /**
   * The rule form takes a refusal it can mark on its own rows (TMMAPI-0205's currency refusal on a
   * target) and returns true; otherwise the panel shows its own error.
   */
  onRefused?: (error: unknown) => boolean;
}

function defaultRange(timeZone: string): { from: string; to: string } {
  const to = civilInZone(new Date(), timeZone);
  return { from: addCivilDays(to, -89), to };
}

function categoryName(
  envelopes: ReturnType<typeof useOwnedEnvelopes>['envelopes'],
  envelopeId: number,
  categoryId: number | null,
): string | null {
  if (categoryId === null) return null;
  const envelope = envelopes.find((row) => row.id === envelopeId);
  const cat = envelope?.categories?.find((c) => c.id === categoryId);
  return cat?.name ?? null;
}

/**
 * Dry-run dialog (TMMAPP-0017) of its own — the rules list opens it. The rule editor does not: it
 * shows `DryRunBody` in place of its form, in its own dialog (TMMAPP-0082, DLG-005).
 */
export function DryRunPanel(props: DryRunPanelProps) {
  const fullScreen = useDialogFullScreen();
  return (
    <Dialog
      open
      onClose={props.onClose}
      fullScreen={fullScreen}
      fullWidth
      maxWidth="md"
      aria-labelledby={DRY_RUN_TITLE_ID}
    >
      <DryRunBody {...props} />
    </Dialog>
  );
}

/**
 * The dry run's content (title, range, Run, matches with would-allocate and shortfall, Close): what
 * a `Dialog` shows, or what the rule editor shows in place of its form. POST
 * `/allocation-rules/dry-run` only — nothing is posted to the register.
 */
export function DryRunBody({
  accountId,
  currency,
  timeZone,
  source,
  onClose,
  onRefused,
}: DryRunPanelProps) {
  const { t } = useTranslation();
  const locale = useLocale();
  const theme = useTheme();
  const md = useMediaQuery(theme.breakpoints.up('md'));
  const { envelopes } = useOwnedEnvelopes();
  const initial = defaultRange(timeZone);
  const [from, setFrom] = useState(initial.from);
  const [to, setTo] = useState(initial.to);
  const [rangeError, setRangeError] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState<DryRun | null>(null);
  // Shown in place of the rule form in a dialog that stays mounted, the dry run takes focus at its
  // first field (the Dry run button that opened it has just unmounted).
  const fromRef = useRef<HTMLInputElement>(null);
  useEffect(() => {
    fromRef.current?.focus();
  }, []);
  const isTransfer =
    'rule' in source ? source.rule.transferAccountId != null : source.transfer === true;

  async function handleRun() {
    const span = civilSpanDays(from, to);
    if (span === null || from > to) {
      setRangeError(t('rules.rangeTooLong'));
      return;
    }
    if (span > MAX_DAYS) {
      setRangeError(t('rules.rangeTooLong'));
      return;
    }
    setRangeError(null);
    setBusy(true);
    setError(null);
    try {
      const next = await allocationRulesApi.dryRun(accountId, source, { from, to });
      setResult(next);
    } catch (err) {
      if (onRefused?.(err) === true) return;
      if (isApiError(err) && err.code === ErrorCodes.SUBSCRIPTION_REQUIRED) {
        setError(t('accounts.errorGeneric'));
      } else if (isApiError(err) && err.code === ErrorCodes.ALLOCATION_CURRENCY_MISMATCH) {
        // An unsaved rule's target into a virtual account in another currency (TMMAPI-0205).
        setError(t('allocations.errorCurrencyMismatch'));
      } else {
        setError(t('accounts.errorGeneric'));
      }
    } finally {
      setBusy(false);
    }
  }

  return (
    <>
      <DialogTitle id={DRY_RUN_TITLE_ID}>{t('rules.dryRun')}</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 0.5 }}>
          <Typography variant="body2" sx={{ color: 'text.secondary' }}>
            {t('rules.dryRunNote')}
          </Typography>
          <Stack direction={{ xs: 'column', md: 'row' }} sx={{ gap: 2 }}>
            <TextField
              type="date"
              inputRef={fromRef}
              label={t('rules.from')}
              value={from}
              onChange={(event) => setFrom(event.target.value)}
              slotProps={{ inputLabel: { shrink: true } }}
            />
            <TextField
              type="date"
              label={t('rules.to')}
              value={to}
              onChange={(event) => setTo(event.target.value)}
              slotProps={{ inputLabel: { shrink: true } }}
            />
            <Button variant="contained" onClick={() => void handleRun()} disabled={busy}>
              {t('rules.run')}
            </Button>
          </Stack>
          {rangeError !== null && <Alert severity="error">{rangeError}</Alert>}
          {error !== null && <Alert severity="error">{error}</Alert>}
          {result !== null && (
            <>
              <Typography variant="body1">
                {t('rules.dryRunSummary', {
                  matched: result.matchedCount,
                  considered: result.consideredCount,
                })}
              </Typography>
              {isTransfer && <Alert severity="info">{t('rules.dryRunTransferNote')}</Alert>}
              {result.matched.length === 0 ? (
                <Typography variant="body2">{t('rules.dryRunEmpty')}</Typography>
              ) : md ? (
                <Table size="small" aria-label={t('rules.dryRun')}>
                  <TableHead>
                    <TableRow>
                      <TableCell>{t('rules.colDate')}</TableCell>
                      <TableCell>{t('rules.colPayee')}</TableCell>
                      <TableCell align="right">{t('rules.colAmount')}</TableCell>
                      {!isTransfer && <TableCell>{t('rules.colWould')}</TableCell>}
                      {!isTransfer && <TableCell>{t('rules.colShortfall')}</TableCell>}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {result.matched.map((row) => (
                      <TableRow key={row.transactionId}>
                        <TableCell>
                          <TabularText>{formatCivilDate(row.postedOn, locale)}</TabularText>
                        </TableCell>
                        <TableCell>{row.payee}</TableCell>
                        <TableCell align="right">
                          <MoneyText minor={row.amountMinor} currency={currency} />
                        </TableCell>
                        {!isTransfer && (
                          <TableCell>
                            <WouldLines match={row} envelopes={envelopes} currency={currency} />
                          </TableCell>
                        )}
                        {!isTransfer && (
                          <TableCell>
                            <Shortfall
                              minor={row.shortfallMinor}
                              currency={currency}
                              locale={locale}
                              label={t('rules.shortfall')}
                              help={t('rules.shortfallHelp')}
                            />
                          </TableCell>
                        )}
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              ) : (
                <Stack spacing={1.5}>
                  {result.matched.map((row) => (
                    <Paper key={row.transactionId} variant="outlined" sx={{ p: 1.5 }}>
                      <Stack spacing={0.75}>
                        <TabularText component="div">
                          {formatCivilDate(row.postedOn, locale)}
                        </TabularText>
                        <Typography variant="body1">{row.payee}</Typography>
                        <MoneyText minor={row.amountMinor} currency={currency} />
                        {!isTransfer && (
                          <WouldLines match={row} envelopes={envelopes} currency={currency} />
                        )}
                        {!isTransfer && (
                          <Shortfall
                            minor={row.shortfallMinor}
                            currency={currency}
                            locale={locale}
                            label={t('rules.shortfall')}
                            help={t('rules.shortfallHelp')}
                          />
                        )}
                      </Stack>
                    </Paper>
                  ))}
                </Stack>
              )}
            </>
          )}
        </Stack>
      </DialogContent>
      <DialogActions>
        <DialogDoneButton onClick={onClose} />
      </DialogActions>
    </>
  );
}

function WouldLines({
  match,
  envelopes,
  currency,
}: {
  match: DryRunMatch;
  envelopes: ReturnType<typeof useOwnedEnvelopes>['envelopes'];
  currency: string | null;
}) {
  return (
    <Stack spacing={0.25}>
      {match.wouldAllocate.map((line, index) => {
        const { name } = envelopeLabel(envelopes, line.envelopeId);
        const cat = categoryName(envelopes, line.envelopeId, line.categoryId);
        return (
          <Typography key={`${line.envelopeId}-${index}`} variant="body2">
            {name}
            {cat !== null ? ` · ${cat}` : ''}{' '}
            <MoneyParts minor={line.amountMinor} currency={currency} />
          </Typography>
        );
      })}
    </Stack>
  );
}

function Shortfall({
  minor,
  currency,
  label,
  help,
}: {
  minor: number;
  currency: string | null;
  locale: string;
  label: string;
  help: string;
}) {
  if (minor <= 0) return null;
  return (
    <Stack direction="row" sx={{ gap: 0.75, alignItems: 'center', flexWrap: 'wrap' }}>
      <MoneyText minor={minor} currency={currency} />
      <Tooltip describeChild title={help}>
        <Chip size="small" label={label} />
      </Tooltip>
    </Stack>
  );
}
